/**
 * Cliente API de tenencias.
 *
 * Sprint 13 S13-05 + S13-07 (migrado desde frontend-superadmin).
 * - listTenencias: alimenta el selector del modal de creacion de
 *   compensaciones (Tesoreria) y el listado de la pagina
 *   /admin/tenencias (Comision).
 * - cerrarTenencia: flujo ex-vecino (S11-V2-13). RBAC backend
 *   ajustado a comision_o_tesorero en S13-07.
 */
import { apiFetch } from './client';
import type {
  CerrarTenenciaInput,
  CerrarTenenciaResponse,
  ListTenenciasFiltros,
  TenenciaConPersonaLote,
} from './types';

/**
 * Tipos de tenencia OCUPANTE — quien vive en la casa (espejo de
 * `TIPOS_OCUPANTE` del backend, `models/tenencia.py`). El
 * `propietario_no_residente` queda fuera: es dueño pero no vive, no
 * onboarda el hogar arrendado. Fuente única para el portal (onboarding
 * bloqueante en ProtectedRoute + selección de tenencia en el wizard).
 */
export const TIPOS_OCUPANTE: readonly string[] = [
  'propietario_residente',
  'arrendatario',
  'comodatario',
];

/** Tipo de ocupante que el vecino confirma en el onboarding. */
export type TipoOcupante =
  | 'propietario_residente'
  | 'arrendatario'
  | 'comodatario';

/** Etiqueta usuario-facing de cada tipo de ocupante (convención #13). */
export const TIPO_OCUPANTE_LABEL: Record<TipoOcupante, string> = {
  propietario_residente: 'Propietario',
  arrendatario: 'Arrendatario',
  comodatario: 'Comodatario',
};

export function listTenencias(
  filtros: ListTenenciasFiltros = {},
): Promise<TenenciaConPersonaLote[]> {
  const params = new URLSearchParams();
  if (filtros.lote_id) params.set('lote_id', filtros.lote_id);
  if (filtros.persona_id) params.set('persona_id', filtros.persona_id);
  if (filtros.activas !== undefined) params.set('activas', String(filtros.activas));
  if (filtros.sector) params.set('sector', filtros.sector);
  const qs = params.toString();
  return apiFetch<TenenciaConPersonaLote[]>(`/tenencias/${qs ? `?${qs}` : ''}`);
}

export function cerrarTenencia(
  tenenciaId: string,
  data: CerrarTenenciaInput,
): Promise<CerrarTenenciaResponse> {
  return apiFetch<CerrarTenenciaResponse>(
    `/tenencias/${tenenciaId}/cerrar`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

/** Sprint 18 P3 B3: el vecino cierra su propia tenencia (botón
 *  "Dejé el barrio"). Reusa el mismo schema que el endpoint de
 *  comisión. Backend ejecuta los 5 side-effects A3 (D-P3-1). */
export function cerrarMiTenencia(
  tenenciaId: string,
  data: CerrarTenenciaInput,
): Promise<CerrarTenenciaResponse> {
  return apiFetch<CerrarTenenciaResponse>(
    `/me/tenencias/${tenenciaId}/cerrar`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

/** Sprint 18 P24: el propietario que se muda SIN vender convierte su
 *  tenencia a propietario_no_residente (no cierra; inactiva su hogar). */
export function cambiarMiTenenciaANoResidente(
  tenenciaId: string,
): Promise<CerrarTenenciaResponse> {
  return apiFetch<CerrarTenenciaResponse>(
    `/me/tenencias/${tenenciaId}/cambiar-a-no-residente`,
    { method: 'POST' },
  );
}

/** Forma del `TenenciaRead` del backend (espejo de schemas/tenencia.py). */
export interface TenenciaRead {
  id: string;
  lote_id: string;
  persona_id: string;
  tipo: string;
  desde: string;
  hasta: string | null;
  es_actual: boolean;
  responsable_pago_cuotas: boolean;
  motivo_cierre: string | null;
  notas: string | null;
  creado_at: string;
  actualizado_at: string;
}

/** Onboarding: el titular confirma/corrige su situación en la casa
 *  (Propietario / Arrendatario / Comodatario). Cambio directo, sin
 *  validación de la comisión (metadata descriptiva). */
export function confirmarTipoMiTenencia(
  tenenciaId: string,
  tipo: TipoOcupante,
): Promise<TenenciaRead> {
  return apiFetch<TenenciaRead>(`/me/tenencias/${tenenciaId}/tipo`, {
    method: 'PATCH',
    body: JSON.stringify({ tipo }),
  });
}

export interface ResumenArrendado {
  arrendatario_nombre: string | null;
  n_residentes: number;
  n_vehiculos: number;
  n_llaveros: number;
  n_controles: number;
}

/** Sprint 18 P23: resumen del lote arrendado para el propietario no
 *  residente (cantidades + nombre del arrendatario, sin PII del hogar). */
export function getResumenArrendado(
  loteId: string,
): Promise<ResumenArrendado> {
  return apiFetch<ResumenArrendado>(`/me/lotes/${loteId}/resumen-arrendado`);
}
